import { useQuery } from "@tanstack/react-query";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import { logger } from "@/lib/logger";
import useReleaseNotesStore from "@/stores/release-notes.store";
import {
  ReleaseNote,
  parseReleaseNoteResponse,
} from "@/models/release-notes/ReleaseNote";
import { restError } from "@/models/client/RestResponse";
import {
  hasUnreadReleaseNote,
  visibleReleaseNotes,
} from "@/lib/releaseNotesVisibility";

const RELEASE_NOTES_KEY = PATH_KEYS.releaseNote;

export const useReleaseNotes = () => {
  const releaseNoteService = useItemService((services) => services.releaseNote);

  return useQuery<ReleaseNote[], Error>({
    queryKey: [RELEASE_NOTES_KEY],
    queryFn: async () => {
      const response = await releaseNoteService.getAll();
      if (response.success) {
        return response.value.map(parseReleaseNoteResponse);
      }
      logger.error("Failed to load release notes", {
        operation: "getReleaseNotes",
        error: response.feedback,
        status: response.status,
      });
      throw restError(response);
    },
  });
};

/**
 * Whether a release note the user can see is newer than the one they last saw.
 * Drives the unread dot on the profile menu; the dot clears once the changelog
 * dialog is opened (which marks the newest note as seen). Technical notes only
 * count when the user has switched them on.
 */
export const useUnreadReleaseNote = (): { hasUnread: boolean } => {
  const { data: notes } = useReleaseNotes();
  const lastSeenReleaseNoteId = useReleaseNotesStore(
    (state) => state.lastSeenReleaseNoteId
  );
  const showTechnical = useReleaseNotesStore(
    (state) => state.showTechnicalReleaseNotes
  );

  if (!notes) return { hasUnread: false };
  const visible = visibleReleaseNotes(notes, showTechnical);
  return {
    hasUnread: hasUnreadReleaseNote(notes, visible, lastSeenReleaseNoteId),
  };
};
